import React from 'react'
import Link from 'next/link'

export interface BreadcrumbItem {
  /** Human-readable label shown in the visible trail. */
  label: string
  /** Absolute path for the link. Omit for the current (last) item — it
   *  renders as plain text instead of a link, which matches the rich-result
   *  guidance that the trailing crumb should not be a link. */
  href?: string
}

const SITE_URL = 'https://microplasticsapp.com'

/**
 * Renders visible breadcrumbs + matching BreadcrumbList JSON-LD. Single source
 * of truth for breadcrumb styling across the site — used by every top-level
 * page AND by BlogPostLayout so users get consistent visual hierarchy
 * regardless of where they are.
 *
 * Visual style follows modern breadcrumb best practice:
 * - Chevron (›) separator rather than slash — clearer visual hierarchy,
 *   conventional in Material Design and Apple HIG patterns.
 * - Muted slate separators (gray-400) so they read as connectors, not content.
 * - Current page item is non-clickable, gray-900, line-clamp-1 with min-w-0
 *   so long article titles truncate cleanly inside flex containers.
 * - 32px min-height tap target on link items for accessibility without
 *   stretching the breadcrumb visually.
 */
export default function Breadcrumbs({
  items,
  className = '',
}: {
  items: BreadcrumbItem[]
  /** Optional override for the wrapping nav (e.g. add `mb-6` on blog posts). */
  className?: string
}) {
  const allItems: BreadcrumbItem[] = [{ label: 'Home', href: '/' }, ...items]

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: allItems.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.label,
      // Spec: every ListItem should have `item` except possibly the trailing
      // one. We attach `item` whenever a path is supplied; the renderer
      // omits the link for the last crumb regardless.
      ...(item.href ? { item: `${SITE_URL}${item.href}` } : {}),
    })),
  }

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <nav
        aria-label="Breadcrumb"
        className={`text-sm text-gray-500 ${className}`.trim()}
      >
        <ol className="flex flex-wrap items-center gap-x-1.5 gap-y-1 min-w-0">
          {allItems.map((item, i) => {
            const isLast = i === allItems.length - 1
            return (
              <React.Fragment key={`${item.label}-${i}`}>
                <li className={isLast ? 'min-w-0 flex-1 sm:flex-initial' : 'shrink-0'}>
                  {isLast || !item.href ? (
                    <span
                      className="text-gray-900 font-medium line-clamp-1 block"
                      aria-current="page"
                    >
                      {item.label}
                    </span>
                  ) : (
                    <Link
                      href={item.href}
                      className="hover:text-forest transition-colors duration-200 inline-flex items-center min-h-[32px] py-1 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-forest focus-visible:ring-offset-2"
                    >
                      {item.label}
                    </Link>
                  )}
                </li>
                {!isLast && (
                  <li
                    aria-hidden="true"
                    className="text-gray-400 shrink-0 select-none"
                  >
                    ›
                  </li>
                )}
              </React.Fragment>
            )
          })}
        </ol>
      </nav>
    </>
  )
}
